// @ts-strict-ignore
import React, { useCallback, useMemo, useState } from 'react'
import Button from 'components/Button'
import FormSubmitButton from 'components/FormSubmitButton'
import Condition from 'components/Condition'
import ResponsiveDialog from 'components/ResponsiveDialog'
import TextArea from 'components/TextArea'
import TextField from 'components/TextField'
import { Collapse, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemText, Typography, Grid } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ReactMarkdown from 'react-markdown'
import ScreenshotUpload
  from 'scenes/Job/components/JobApplicationDialog/components/ScreenshotUpload'
import { useCodeTestBrowserForm } from 'components/CodeTestBrowser/useCodeTestBrowserForm'
import { graphql, useLazyLoadQuery } from 'react-relay'
import CodeSubmissionDialog from 'components/CodeTestBrowser/CodeSubmissionDialog'
import { ProjectSubmissionDialog_GetProjectsQuery } from '__generated__/ProjectSubmissionDialog_GetProjectsQuery.graphql'
import { makeStyles } from 'tss-react/mui'
import { FormProvider } from 'react-hook-form'

const useStyles = makeStyles()(theme => ({
  collapse: {
    padding: theme.spacing(3),
    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
    background: 'rgba(0, 0, 0, 0.04)',
  },
  openItem: {
    background: 'rgba(0, 0, 0, 0.04) !important',
  },
  codeTestDescription: {
    marginBottom: theme.spacing(2),
    lineHeight: '1.5em',
    '& > *:first-child': {
      marginTop: '0',
    },
    '& > h1': {
      fontSize: '1em',
    },
    '& > h2': {
      fontSize: '1em',
    },
    '& > h3': {
      fontSize: '1em',
    },
  },
}))

function ProjectSubmissionDialog({ onClose }: { onClose: () => void }) {
  const { classes } = useStyles()
  const theme = useTheme()
  const [selectedCodeTestId, setSelectedCodeTestId] = useState(undefined)
  const [isCodeSubmissionOpen, setIsCodeSubmissionOpen] = useState(false)
  const onCloseHandler = useCallback(() => {
    setIsCodeSubmissionOpen(false)
    onClose()
  }, [setIsCodeSubmissionOpen, onClose])
  const form = useCodeTestBrowserForm(onCloseHandler)
  const { useFormField, submit } = form
  const projectIdField = useFormField('projectId', 'selection')

  // TODO refactor it to use all projects, not only 20
  const projectsData = useLazyLoadQuery<ProjectSubmissionDialog_GetProjectsQuery>(graphql`
    query ProjectSubmissionDialog_GetProjectsQuery($screening: Boolean!, $perPage: Int) {
      projects(screening: $screening, first: $perPage) {
        nodes {
          id
          title
          description
          custom
          ...CodeSubmissionDialog_Project
          ...useCodeTestBrowserForm_Project
        }
      }
    }
  `, {
    screening: false,
    perPage: 20,
  }, {
    fetchPolicy: 'store-and-network',
  })

  const filteredProjectsData = useMemo(() => (
    projectsData?.projects?.nodes.filter(n => !n.custom) || []
  ), [projectsData])

  return (
    <React.Fragment>
      <ResponsiveDialog open onClose={onClose}>
        <DialogTitle>
          Add sample work
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" className={classes.codeTestDescription}>
            One of the strongest validations of expertise is showing work you have done.
            Share a software project you have written or if you don't have an existing project,
            you can build and submit our simple app per the specifiction below.
          </Typography>

          <List>
            <ListItemButton
              onClick={() => setSelectedCodeTestId(undefined)}
              className={selectedCodeTestId === undefined ? classes.openItem : undefined}
              data-cy="your-project"
            >
              <ListItemText primary="Your Project to Share" />
            </ListItemButton>

            <Collapse in={selectedCodeTestId === undefined}>
              <div className={classes.collapse}>
                <FormProvider {...form}>
                  <form onSubmit={submit}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          {...useFormField('title', 'typing')}
                          placeholder="Project Title"
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <TextField
                          {...useFormField('url', 'typing')}
                          placeholder="Project repository URL (e.g. Github)"
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <ScreenshotUpload
                          {...useFormField('screenshotUrl', 'selection')}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextArea
                          {...useFormField('description', 'typing')}
                          style={{ minHeight: 120 }}
                          label="Additional notes"
                          placeholder="Additional information about the project... (optional)"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormSubmitButton
                          onClick={submit}
                          dataCy="submit-custom-project"
                          label="Submit"
                        />
                      </Grid>
                    </Grid>
                  </form>
                </FormProvider>
              </div>
            </Collapse>

            {filteredProjectsData.map(codeTest => (
              <React.Fragment key={codeTest.id}>
                <ListItemButton
                  onClick={() => projectIdField.input.onChange(codeTest?.id)}
                  className={codeTest.id === selectedCodeTestId ? classes.openItem : undefined}
                >
                  <ListItemText primary={codeTest.title} />
                </ListItemButton>

                <Collapse in={codeTest.id === projectIdField.input.value}>
                  <div className={classes.collapse}>
                    <div className={classes.codeTestDescription}>
                      <ReactMarkdown source={codeTest.description || 'No description.'} />
                    </div>

                    <div>
                      <Button color="primary" onClick={() => setIsCodeSubmissionOpen(true)} data-cy="submit-existing-project">
                        Submit a project
                      </Button>
                      <Condition condition={codeTest.id === projectIdField.input.value && isCodeSubmissionOpen}>
                        <CodeSubmissionDialog
                          projectFragmentRef={codeTest}
                          onClose={() => setIsCodeSubmissionOpen(false)}
                        />
                      </Condition>
                    </div>
                  </div>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} data-cy="cancel">
            Cancel
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </React.Fragment>
  )
}

export default React.memo(ProjectSubmissionDialog)
